import { AnalysisDisplayPublic, GetAnalyses, ModelType } from '@livv/models';
import { Box, SxProps, Typography } from '@mui/material';
import groupBy from 'lodash/groupBy';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import BlockTitle from '../components/BlockTitle';
import AnalysisCard from './AnalysisCard';
import { useFreemiumDialog } from '@utils/context/freemiumDialog';

interface AnalysesProps {
    analysesOutput?: GetAnalyses['output'];
    focusedAnalysisId?: string;
    sx: SxProps;
}

interface AnalysisGroups {
    [key: string]: AnalysisDisplayPublic[];
}

const sortAnalysisByPosition = (
    analysis1: AnalysisDisplayPublic,
    analysis2: AnalysisDisplayPublic,
) => analysis1.position - analysis2.position;

// sorting group's analysis depending on position
// then groups depending on their first analysis position
const sortAnalysisGroups = (analysisGroups: AnalysisGroups): [string, AnalysisDisplayPublic[]][] =>
    Object.entries(analysisGroups).sort((group1, group2) => {
        const analysisDisplayPublic1 = group1[1];
        const analysisDisplayPublic2 = group2[1];
        // Sort analysis by position in each group
        analysisDisplayPublic1.sort(sortAnalysisByPosition);
        analysisDisplayPublic2.sort(sortAnalysisByPosition);

        // After each analysis group is sorted, sort groups by position of their first analysis
        return sortAnalysisByPosition(analysisDisplayPublic1[0], analysisDisplayPublic2[0]);
    });

const Analyses: FC<AnalysesProps> = ({ analysesOutput, focusedAnalysisId, sx }) => {
    const { t } = useTranslation('keyword');
    const { t: decisionTranslate } = useTranslation('decision');
    const { openFreemiumDialog } = useFreemiumDialog();

    if (!analysesOutput) {
        return null;
    }

    if (analysesOutput.status === 'error') {
        return (
            <Typography component="div" sx={{ pb: 2 }}>
                {decisionTranslate('analysisError')}
            </Typography>
        );
    }

    const { analyses, isPremiumContent } = analysesOutput;

    if (!analyses.length) {
        return null;
    }

    const analysesGroupedByKeyword: AnalysisGroups = groupBy(
        analyses,
        ({ treeTitle, pathKeywords }) => `${treeTitle}/${pathKeywords.join('/')}`,
    );

    const sortedAnalysisGroups: [string, AnalysisDisplayPublic[]][] =
        sortAnalysisGroups(analysesGroupedByKeyword);

    return (
        <>
            <Typography sx={sx} variant="h2" gutterBottom>
                {decisionTranslate('resources.analysis')}
            </Typography>
            <Box>
                <BlockTitle
                    modelType={ModelType.ANALYSIS}
                    title={t('analyses', { count: analyses.length })}
                />
                {sortedAnalysisGroups.map(([keyword, analysesGroup]) => (
                    <AnalysisCard
                        key={keyword}
                        analyses={analysesGroup}
                        focusedAnalysisId={focusedAnalysisId}
                        isFocused={Boolean(
                            focusedAnalysisId &&
                                analysesGroup.some(
                                    ({ analysis }) => focusedAnalysisId === analysis?.id,
                                ),
                        )}
                        isPremiumContent={isPremiumContent}
                        onPremiumClick={openFreemiumDialog}
                    />
                ))}
            </Box>
        </>
    );
};

export default Analyses;
